import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

const buttonVariants = cva(
  'inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium transition-all duration-150 ease-in-out disabled:pointer-events-none disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[var(--color-primary)]',
  {
    variants: {
      variant: {
        primary:
          'bg-[var(--color-primary)] text-white hover:brightness-110 active:brightness-95',
        secondary:
          'bg-transparent text-[var(--color-text)] border border-[var(--color-border)] hover:bg-gray-100 hover:border-gray-400',
        ghost:
          'bg-transparent border border-[var(--color-border)] text-[var(--color-text)] hover:bg-gray-100 hover:border-gray-400',
        danger: 'bg-[var(--color-rise)] text-white hover:brightness-110',
        link: 'underline-offset-4 hover:underline text-[var(--color-primary)]',
      },
      size: {
        sm: 'h-9 px-3 text-sm',       /* 36px — 모바일 터치 최소 영역 확보 */
        md: 'h-10 px-4 text-sm',      /* 40px */
        lg: 'h-12 px-6 text-base',    /* 48px */
        icon: 'h-10 w-10',
      },
      fullWidth: {
        true: 'w-full',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, fullWidth, loading, disabled, children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(buttonVariants({ variant, size, fullWidth }), className)}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
        ) : null}
        {children}
      </button>
    );
  },
);
Button.displayName = 'Button';
